import React, { useState, useMemo, useEffect } from 'react';
import axios from 'axios';
import {
  X, Check, AlertTriangle, ChevronLeft, Dumbbell, CalendarPlus,
} from 'lucide-react';
import { toIsoUtc } from '../lib/datetime';
import { homeGroundLabel } from '../lib/venue';

/**
 * SeasonTraining — "Set up the season's training" (onboarding push slice 3,
 * Aug 2026). No new backend: this is the v13 recurrence machinery surfaced.
 * Pick team(s) × day(s) × time × until-date → one weekly series per team×day
 * via the existing POST /events recurrence path (which caps at 60 occurrences
 * per series and sends ONE push per series, not one per session).
 *
 * Venue defaults to the club home ground (with its coords). If the user types
 * a different venue, we geocode the text once (GET /geocode) so maps + weather
 * still work — same as the single training form's save flow.
 */

const DAYS = [
  { dow: 1, label: 'Mon' }, { dow: 2, label: 'Tue' }, { dow: 3, label: 'Wed' },
  { dow: 4, label: 'Thu' }, { dow: 5, label: 'Fri' }, { dow: 6, label: 'Sat' },
  { dow: 0, label: 'Sun' },
];

const MAX_SERIES_OCCURRENCES = 60; // mirror of MAX_SERIES in comms.js

const pad = (n) => String(n).padStart(2, '0');
const toLocalYmd = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Mirror of ClubHub's teamTitleLabel: "U11s" → "U11" in composed titles.
const teamTitleLabel = (name) => (name || '').replace(/\bU(\d+)s\b/gi, 'U$1');

// First occurrence of `dow` at `time` (HH:MM), today included only if the
// time hasn't passed yet. Returns a local "YYYY-MM-DDTHH:MM" string.
function firstOccurrence(dow, time) {
  const now = new Date();
  const d = new Date(now);
  d.setDate(d.getDate() + ((dow - d.getDay() + 7) % 7));
  const [hh, mm] = time.split(':').map(Number);
  if (toLocalYmd(d) === toLocalYmd(now) && (hh < now.getHours() || (hh === now.getHours() && mm <= now.getMinutes()))) {
    d.setDate(d.getDate() + 7);
  }
  return `${toLocalYmd(d)}T${time}`;
}

// How many weekly occurrences between firstLocal and untilYmd (inclusive).
function countOccurrences(firstLocal, untilYmd) {
  if (!firstLocal || !untilYmd) return 0;
  const first = new Date(`${firstLocal.slice(0, 10)}T12:00:00`);
  const until = new Date(`${untilYmd}T12:00:00`);
  if (until < first) return 0;
  return Math.min(Math.floor((until - first) / (7 * 24 * 60 * 60 * 1000)) + 1, MAX_SERIES_OCCURRENCES);
}

export default function SeasonTraining({ clubSlug, teams = [], club, accentColor, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [step, setStep] = useState('form'); // form | results

  const [teamIds, setTeamIds] = useState(() => new Set(teams.length === 1 ? [teams[0].id] : []));
  const [days, setDays] = useState(() => new Set());
  const [time, setTime] = useState('18:00');
  const [until, setUntil] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 12 * 7); // ~a school term of weekly sessions
    return toLocalYmd(d);
  });

  // WT-21: the home ground once, not "Name, Name, …" (lib/venue.js).
  const defaultVenue = homeGroundLabel(club);
  const [venue, setVenue] = useState(defaultVenue);
  const venueIsHome = venue.trim() === defaultVenue.trim() && !!defaultVenue;

  const [results, setResults] = useState([]);

  // QA must-fix (28 Aug): running this twice for the same team silently
  // doubles a whole season. Look up which teams already have an upcoming
  // recurring training series and warn on their chips.
  const [teamsWithSeries, setTeamsWithSeries] = useState(new Set());
  useEffect(() => {
    let alive = true;
    axios.get(`/clubs/${clubSlug}/events`, { params: { type: 'training' } })
      .then(r => {
        if (!alive) return;
        const now = Date.now();
        const ids = new Set(
          (Array.isArray(r.data) ? r.data : [])
            .filter(e => e.series_id && e.team_id && new Date(e.starts_at).getTime() > now)
            .map(e => String(e.team_id))
        );
        setTeamsWithSeries(ids);
      })
      .catch(() => { /* warning-only lookup — never blocks the wizard */ });
    return () => { alive = false; };
  }, [clubSlug]);

  const selectedWithSeries = teams.filter(t => teamIds.has(t.id) && teamsWithSeries.has(String(t.id)));

  const minUntil = toLocalYmd(new Date());

  const summary = useMemo(() => {
    const seriesCount = teamIds.size * days.size;
    if (!seriesCount || !time || !until) return null;
    let sessions = 0;
    for (const dow of days) sessions += countOccurrences(firstOccurrence(dow, time), until);
    sessions *= teamIds.size;
    return { seriesCount, sessions };
  }, [teamIds, days, time, until]);

  const toggle = (set, setter, val) => setter(prev => {
    const n = new Set(prev);
    n.has(val) ? n.delete(val) : n.add(val);
    return n;
  });

  const handleCreate = async () => {
    setBusy(true); setError(null);
    try {
      // Venue coords: home ground's when the text is untouched; otherwise one
      // geocode of the typed text (empty result ⇒ no coords, never invented).
      let venueLat = venueIsHome ? (club?.home_ground_lat ?? null) : null;
      let venueLng = venueIsHome ? (club?.home_ground_lng ?? null) : null;
      const venueText = venue.trim();
      if (venueText && !venueIsHome) {
        try {
          const geo = await axios.get('/geocode', { params: { q: venueText, limit: 1 } });
          if (geo.data?.[0]?.lat != null) {
            venueLat = geo.data[0].lat;
            venueLng = geo.data[0].lng;
          }
        } catch { /* no coords — maps/weather just won't show */ }
      }

      const out = [];
      for (const teamId of teamIds) {
        const team = teams.find(t => t.id === teamId);
        for (const dow of [...days].sort((a, b) => (a === 0 ? 7 : a) - (b === 0 ? 7 : b))) {
          const startsLocal = firstOccurrence(dow, time);
          const isWeekend = dow === 0 || dow === 6;
          // Duration mirrors autoEndFromStart: weekend training 2h, midweek 1h.
          const [hh, mm] = time.split(':').map(Number);
          const end = new Date(`${startsLocal.slice(0, 10)}T${pad(hh)}:${pad(mm)}`);
          end.setHours(end.getHours() + (isWeekend ? 2 : 1));
          const endsLocal = `${toLocalYmd(end)}T${pad(end.getHours())}:${pad(end.getMinutes())}`;
          const title = `${teamTitleLabel(team?.name)} ${isWeekend ? 'Training' : 'Midweek Training'}`.trim();
          const dayLabel = DAYS.find(d => d.dow === dow)?.label || '';
          try {
            const res = await axios.post(`/clubs/${clubSlug}/events`, {
              title,
              eventType: 'training',
              location: venueText || null,
              venueLat, venueLng,
              startsAt: toIsoUtc(startsLocal),
              endsAt: toIsoUtc(endsLocal),
              teamId,
              teamName: team?.name,
              recurrence: { type: 'weekly', until },
            });
            out.push({ ok: true, title, dayLabel, count: res.data?.series_count || 1 });
          } catch (err) {
            out.push({ ok: false, title, dayLabel, error: err?.response?.data?.error || err?.message || 'Failed' });
          }
        }
      }
      setResults(out);
      setStep('results');
      // The created series now exist — every previously selected team would be
      // a duplicate if "Add more" re-created it (QA must-fix, 28 Aug). Clear
      // the selections and refresh the already-has-training warning set.
      setTeamsWithSeries(prev => new Set([...prev, ...[...teamIds].map(String)]));
      setTeamIds(new Set());
      setDays(new Set());
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Something went wrong creating the series.');
    } finally { setBusy(false); }
  };

  const canCreate = teamIds.size > 0 && days.size > 0 && !!time && !!until && (summary?.sessions || 0) > 0;

  return (
    <div className="modal-overlay" onClick={() => onClose(step === 'results')}>
      <div className="modal-card modal-md" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3><Dumbbell size={16} /> Set up the season's training</h3>
          <button className="btn btn-ghost btn-icon" onClick={() => onClose(step === 'results')}><X size={18} /></button>
        </div>

        {step === 'form' && (
          <>
            <p className="modal-subtitle">
              Pick who trains, which evenings and until when — we'll create the whole
              season as weekly sessions. Each one can be edited or cancelled on its own
              afterwards, and "delete this &amp; future" ends a series early.
            </p>

            <div className="form-group">
              <label className="label">Team(s)</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {teams.map(t => (
                  <button key={t.id} type="button"
                    className={`btn btn-sm ${teamIds.has(t.id) ? 'btn-primary' : 'btn-secondary'}`}
                    style={teamIds.has(t.id) ? { background: accentColor } : undefined}
                    onClick={() => toggle(teamIds, setTeamIds, t.id)}>
                    {teamIds.has(t.id) && <Check size={13} />} {t.name}
                  </button>
                ))}
              </div>
              {selectedWithSeries.length > 0 && (
                <p style={{ fontSize: 12, color: '#B07D2B', marginTop: 6 }}>
                  <AlertTriangle size={12} /> {selectedWithSeries.map(t => t.name).join(', ')}{' '}
                  already {selectedWithSeries.length === 1 ? 'has' : 'have'} weekly training set up —
                  this will create extra sessions alongside the existing ones.
                </p>
              )}
            </div>

            <div className="form-group">
              <label className="label">Day(s)</label>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {DAYS.map(d => (
                  <button key={d.dow} type="button"
                    className={`btn btn-sm ${days.has(d.dow) ? 'btn-primary' : 'btn-secondary'}`}
                    style={days.has(d.dow) ? { background: accentColor } : undefined}
                    onClick={() => toggle(days, setDays, d.dow)}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <div className="form-group" style={{ minWidth: 120 }}>
                <label className="label">Start time</label>
                <input type="time" className="input input-sm" value={time} onChange={e => setTime(e.target.value)} />
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Sessions run 1h midweek, 2h at weekends.
                </p>
              </div>
              <div className="form-group" style={{ minWidth: 150 }}>
                <label className="label">Until</label>
                <input type="date" className="input input-sm" value={until} min={minUntil} onChange={e => setUntil(e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="label">Venue</label>
              <input className="input input-sm" value={venue} placeholder="e.g. the club ground"
                onChange={e => setVenue(e.target.value)} />
              {venueIsHome && (
                <p style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Your home ground — change it if training happens somewhere else.
                </p>
              )}
            </div>

            {summary && (
              <p style={{ fontSize: 13, marginBottom: 12 }}>
                <CalendarPlus size={13} /> Will create <strong>{summary.seriesCount}</strong> weekly
                {' '}series — about <strong>{summary.sessions}</strong> session{summary.sessions === 1 ? '' : 's'} in total.
                Each team gets one notification per series, not one per session.
              </p>
            )}

            <button className="btn btn-primary" style={{ background: accentColor }}
              disabled={busy || !canCreate} onClick={handleCreate}>
              {busy ? 'Creating…' : `Create the season's training`}
            </button>
          </>
        )}

        {step === 'results' && (
          <>
            <p className="modal-subtitle">
              <Check size={14} style={{ color: '#2D7A4F' }} />{' '}
              <strong>{results.filter(r => r.ok).length}</strong> series created
              {results.some(r => !r.ok) && <> · <span style={{ color: '#A03A2E' }}>{results.filter(r => !r.ok).length} failed</span></>}.
              Find them under Training — every session takes Going / Not going responses.
            </p>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: '1px solid var(--border, #E8DFCE)', borderRadius: 8, marginBottom: 12 }}>
              {results.map((r, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '7px 10px', borderBottom: '1px solid var(--border, #F0EAD9)', fontSize: 13 }}>
                  {r.ok
                    ? <Check size={14} style={{ color: '#2D7A4F', flexShrink: 0 }} />
                    : <AlertTriangle size={14} style={{ color: '#A03A2E', flexShrink: 0 }} />}
                  <strong>{r.title}</strong>
                  <span style={{ color: 'var(--text-secondary)' }}>
                    {r.dayLabel}s{r.ok ? ` · ${r.count} session${r.count === 1 ? '' : 's'}` : ` · ${r.error}`}
                  </span>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-ghost" onClick={() => setStep('form')}>
                <ChevronLeft size={14} /> Add more
              </button>
              <button className="btn btn-primary" style={{ background: accentColor }} onClick={() => onClose(true)}>
                Done
              </button>
            </div>
          </>
        )}

        {error && <div className="invite-error" style={{ marginTop: 10 }}><AlertTriangle size={14} /> {error}</div>}
      </div>
    </div>
  );
}
